// ════════════════════════════════════════════════════════════════════════════
//  features/renave/VendaRenave.tsx — fluxo de VENDA no RENAVE (Fatia 3).
//
//  1. CRV atual: após a confirmação do Detran sai CRV novo. O número vem do
//     RENAVE; o código de segurança só no PDF → o operador lê e digita.
//  2. Dados da venda: preenchidos pela NF-e de venda do Gestor (com conferência
//     fiscal de regime normal) ou digitados.
//  3. Registrar venda (saída) → 4. NF-e de venda → 5. ATPVe/CRLV-e.
// ════════════════════════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import { Ban, Download, Eye, FileSearch, FileText, KeyRound, Loader2, ShoppingCart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from '@/components/ui/sonner';
import {
  renaveApi, abrirPdf, baixarPdf, formatarMoeda, SITUACAO,
  type ConferenciaFiscal, type ResultadoOperacao, type VeiculoEstoque,
} from './api';
import { ConferenciaFiscalPainel } from './ConferenciaFiscalPainel';

interface Props {
  v: VeiculoEstoque;
  ocupado: string | null;
  travado: boolean;
  exigirCpf: () => string | null;
  escrever: (rotulo: string, acao: () => Promise<ResultadoOperacao>) => Promise<void>;
  ler: <T,>(rotulo: string, acao: () => Promise<T>, aoTerminar: (t: T) => void) => Promise<void>;
}

const UFS = ['AC','AL','AP','AM','BA','CE','DF','ES','GO','MA','MT','MS','MG','PA','PB','PR','PE','PI','RJ','RN','RS','RO','RR','SC','SP','SE','TO'];

export function VendaRenave({ v, ocupado, travado, exigirCpf, escrever, ler }: Props) {
  const emEstoque = v.situacao === SITUACAO.EmEstoque;
  const vendido   = v.situacao === SITUACAO.Vendido;
  const confirmado = v.renaveEstado === 'CONFIRMADO';

  const [numeroCrv, setNumeroCrv] = useState(v.numeroCrv ?? '');
  const [pdfCodigo, setPdfCodigo] = useState<string | null>(null);
  const [codigo, setCodigo] = useState('');
  const [conferencia, setConferencia] = useState<ConferenciaFiscal | null>(null);
  const [chave, setChave] = useState('');
  const [cancelar, setCancelar] = useState(false);
  const [f, setF] = useState({
    data: new Date().toISOString().slice(0, 10), valor: '', tipoDoc: 'CPF', doc: '', nome: '', email: '',
    cep: '', logradouro: '', numero: '', complemento: '', bairro: '', cidade: '', uf: 'SP', ibge: 0,
  });

  useEffect(() => { setNumeroCrv(v.numeroCrv ?? ''); }, [v.numeroCrv]);
  const set = (k: keyof typeof f, valor: string | number) => setF(x => ({ ...x, [k]: valor }));
  const ocupadoCom = (r: string) => ocupado === r;
  const spin = (r: string, Icone: typeof Eye) =>
    ocupadoCom(r) ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Icone className="mr-2 h-4 w-4" />;

  const preencherPelaNota = (c: ConferenciaFiscal) => {
    setConferencia(c);
    const n = c.nota;
    if (!n) { toast.info(c.avisos[0] ?? 'Nenhuma NF-e de venda encontrada.'); return; }
    const doc = n.documentoDestinatario ?? '';
    setF(x => ({
      ...x,
      data: n.dataEmissao.slice(0, 10), valor: String(n.valorItem),
      tipoDoc: doc.length === 14 ? 'CNPJ' : 'CPF', doc, nome: n.nomeDestinatario ?? '',
      cep: n.cep ?? '', logradouro: n.logradouro ?? '', numero: n.numeroEndereco ?? '', bairro: n.bairro ?? '',
      cidade: n.cidade ?? '', uf: n.uf ?? x.uf, ibge: n.cidadeIbge,
    }));
    setChave(n.chave);
    toast.success(`Dados preenchidos pela NF-e nº ${n.numero}.`);
  };

  const registrarVenda = () => {
    const cpf = exigirCpf(); if (!cpf) return;
    void escrever('venda', () => renaveApi.saida(v.id, {
      dataVenda: f.data, valorVenda: Number(f.valor.replace(',', '.')),
      tipoDocumentoComprador: f.tipoDoc, documentoComprador: f.doc, nomeComprador: f.nome,
      emailComprador: f.email || null, cep: f.cep || null, logradouro: f.logradouro || null, numero: f.numero || null,
      complemento: f.complemento || null, bairro: f.bairro || null, cidade: f.cidade, uf: f.uf,
      cidadeIbge: f.ibge || null, compradorPessoaId: null,
      codigoSegurancaCrv: codigo || null, numeroCrv: numeroCrv || null, cpfOperador: cpf,
    }));
  };

  if (!emEstoque && !vendido) return null;

  return (
    <div className="space-y-4">
      {/* 1. CRV atual ─────────────────────────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">1. CRV atual do veículo</CardTitle>
          <CardDescription>
            Depois da confirmação do Detran sai um CRV novo, e a venda exige o número e o código de segurança dele.
            O número vem do RENAVE; o código está no PDF (leia e digite abaixo).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" size="sm" disabled={!emEstoque || !confirmado || !!ocupado}
              onClick={() => ler('crv', () => renaveApi.crvAtual(v.id), r => { setNumeroCrv(r.numeroCrv ?? ''); setPdfCodigo(r.pdfCodigoSegurancaBase64); toast.success('CRV atual obtido. Abra o PDF para ler o código de segurança.'); })}>
              {spin('crv', KeyRound)}Buscar CRV atual
            </Button>
            {pdfCodigo && (
              <Button variant="outline" size="sm" onClick={() => abrirPdf(pdfCodigo)}>
                <Eye className="mr-2 h-4 w-4" />Abrir PDF do código
              </Button>
            )}
            <Button variant="ghost" size="sm" disabled={!confirmado || !!ocupado}
              onClick={() => ler('crlve', () => renaveApi.crlve(v.id), baixarPdf)}>
              {spin('crlve', Download)}CRLV-e
            </Button>
          </div>
          {!confirmado && emEstoque && (
            <p className="text-xs text-muted-foreground">Disponível depois que o Detran confirmar a entrada (aba Compra → Atualizar situação).</p>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <Rotulo texto="Número do CRV atual">
              <Input value={numeroCrv} inputMode="numeric" maxLength={20} disabled={!emEstoque}
                onChange={e => setNumeroCrv(e.target.value.replace(/\D/g, ''))} />
            </Rotulo>
            <Rotulo texto="Código de segurança do CRV atual (11 dígitos)">
              <Input value={codigo} inputMode="numeric" maxLength={11} autoComplete="off" disabled={!emEstoque}
                placeholder={v.possuiCodigoSegurancaCrv ? 'usar o já gravado' : ''}
                onChange={e => setCodigo(e.target.value.replace(/\D/g, ''))} />
            </Rotulo>
          </div>
        </CardContent>
      </Card>

      {/* 2-3. Dados e registro da venda ─────────────────────────────────── */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">2. Dados da venda {vendido && <span className="text-sm font-normal text-green-600">· venda registrada</span>}</CardTitle>
          <CardDescription>
            Preencha pela NF-e de venda emitida no Gestor (o sistema confere a nota para o regime normal) ou digite.
            O município é convertido para o código do RENAVE pelo nome e UF.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {vendido ? (
            <p className="text-sm">
              Vendido em {v.dataVenda ? new Date(v.dataVenda.slice(0, 10) + 'T00:00:00').toLocaleDateString('pt-BR') : '—'} por{' '}
              <strong>{formatarMoeda(v.valorVenda)}</strong> ao {v.tipoDocumentoComprador ?? 'CPF'} {v.documentoComprador}.
            </p>
          ) : (
            <>
              <Button variant="outline" size="sm" disabled={!!ocupado}
                onClick={() => ler('nf-venda', () => renaveApi.conferenciaFiscal(v.id, 'VENDA'), preencherPelaNota)}>
                {spin('nf-venda', FileSearch)}Preencher pela NF-e de venda
              </Button>
              <div className="grid gap-3 sm:grid-cols-4">
                <Rotulo texto="Data da venda"><Input type="date" value={f.data} onChange={e => set('data', e.target.value)} /></Rotulo>
                <Rotulo texto="Valor (R$)"><Input value={f.valor} inputMode="decimal" onChange={e => set('valor', e.target.value)} /></Rotulo>
                <Rotulo texto="Comprador">
                  <Select value={f.tipoDoc} onValueChange={x => set('tipoDoc', x)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="CPF">CPF</SelectItem><SelectItem value="CNPJ">CNPJ</SelectItem></SelectContent>
                  </Select>
                </Rotulo>
                <Rotulo texto={f.tipoDoc}>
                  <Input value={f.doc} inputMode="numeric" maxLength={f.tipoDoc === 'CNPJ' ? 14 : 11} onChange={e => set('doc', e.target.value.replace(/\D/g, ''))} />
                </Rotulo>
                <Rotulo texto="Nome do comprador *" className="sm:col-span-2"><Input value={f.nome} maxLength={120} onChange={e => set('nome', e.target.value)} /></Rotulo>
                <Rotulo texto="E-mail do comprador *" className="sm:col-span-2"><Input type="email" value={f.email} maxLength={120} onChange={e => set('email', e.target.value)} /></Rotulo>
                <Rotulo texto="CEP"><Input value={f.cep} inputMode="numeric" maxLength={9} onChange={e => set('cep', e.target.value)} /></Rotulo>
                <Rotulo texto="Logradouro" className="sm:col-span-2"><Input value={f.logradouro} onChange={e => set('logradouro', e.target.value)} /></Rotulo>
                <Rotulo texto="Número"><Input value={f.numero} onChange={e => set('numero', e.target.value)} /></Rotulo>
                <Rotulo texto="Bairro"><Input value={f.bairro} onChange={e => set('bairro', e.target.value)} /></Rotulo>
                <Rotulo texto="Complemento"><Input value={f.complemento} onChange={e => set('complemento', e.target.value)} /></Rotulo>
                <Rotulo texto="Cidade"><Input value={f.cidade} onChange={e => set('cidade', e.target.value)} /></Rotulo>
                <Rotulo texto="UF">
                  <Select value={f.uf} onValueChange={x => set('uf', x)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{UFS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </Rotulo>
              </div>
              <Button size="sm" disabled={!emEstoque || !confirmado || travado} onClick={registrarVenda}>
                {spin('venda', ShoppingCart)}Registrar venda no RENAVE
              </Button>
            </>
          )}
          {conferencia && !vendido && <ConferenciaFiscalPainel c={conferencia} />}
        </CardContent>
      </Card>

      {/* 4. NF-e de venda + documentos ──────────────────────────────────── */}
      {vendido && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">3. NF-e de venda e documentos do comprador</CardTitle>
            <CardDescription>
              A chave é conferida para o regime normal antes de ir ao RENAVE (CST, CFOP, destinatário). O ATPVe gerado pelo RENAVE
              dispensa as assinaturas de vendedor e comprador.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {v.chaveNfSaida && <p className="break-all font-mono text-xs">Enviada: {v.chaveNfSaida}</p>}
            <div className="flex flex-wrap gap-2">
              <Input value={chave} onChange={e => { setChave(e.target.value.replace(/\D/g, '')); setConferencia(null); }} maxLength={44}
                inputMode="numeric" placeholder="Chave de 44 dígitos" className="w-[26rem] max-w-full font-mono text-xs" />
              <Button variant="outline" size="sm" disabled={!!ocupado}
                onClick={() => ler('conf-venda', () => renaveApi.conferenciaFiscal(v.id, 'VENDA', chave || undefined), c => { setConferencia(c); if (c.nota) setChave(c.nota.chave); })}>
                {spin('conf-venda', FileSearch)}{chave ? 'Conferir NF-e' : 'Buscar NF-e no Gestor'}
              </Button>
              <Button size="sm" disabled={travado || chave.length !== 44 || (conferencia?.bloqueios.length ?? 0) > 0}
                onClick={() => { const cpf = exigirCpf(); if (cpf) void escrever('nf-saida', () => renaveApi.notaFiscalSaida(v.id, chave, cpf)); }}>
                {spin('nf-saida', FileText)}{v.chaveNfSaida ? 'Trocar chave' : 'Enviar NF-e'}
              </Button>
            </div>
            {conferencia && <ConferenciaFiscalPainel c={conferencia} />}
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" size="sm" disabled={!!ocupado} onClick={() => ler('atpv-venda', () => renaveApi.atpv(v.id), baixarPdf)}>
                {spin('atpv-venda', Download)}ATPVe do comprador
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {vendido && (
        <Card className="border-destructive/30">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Desfazer a venda</CardTitle>
            <CardDescription>Cancela a saída no RENAVE. O veículo volta ao estoque com um registro de estoque novo.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button variant="destructive" size="sm" disabled={travado} onClick={() => setCancelar(true)}>
              {spin('cancelar-venda', Ban)}Cancelar venda
            </Button>
          </CardContent>
        </Card>
      )}

      <AlertDialog open={cancelar} onOpenChange={setCancelar}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancelar a venda no RENAVE?</AlertDialogTitle>
            <AlertDialogDescription>
              A saída é desfeita e a revenda volta a ser a responsável pelo veículo. Cancele também a NF-e de venda, se já foi emitida.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => { const cpf = exigirCpf(); if (cpf) void escrever('cancelar-venda', () => renaveApi.cancelarSaida(v.id, cpf)); }}>
              Cancelar venda
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Rotulo({ texto, className, children }: { texto: string; className?: string; children: React.ReactNode }) {
  return (
    <label className={`block space-y-1.5 ${className ?? ''}`}>
      <span className="text-xs font-medium leading-none">{texto}</span>
      {children}
    </label>
  );
}
